import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertBanner = () => {
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  useEffect(() => {
    // Mock alerts data
    const mockAlerts = [
      {
        id: 'alert-1',
        type: 'critical',
        title: 'Zero Collections Alert',
        message: 'TechCorp Solutions has reported zero collections for the past 2 hours. Immediate attention required.',
        timestamp: new Date(Date.now() - 7200000),
        action: 'Investigate',
        actionType: 'primary'
      },
      {
        id: 'alert-2',
        type: 'warning',
        title: 'Data Entry Delay',
        message: 'Global Enterprises data entry is 45 minutes overdue. Expected submission time was 10:30 AM.',
        timestamp: new Date(Date.now() - 2700000),
        action: 'Contact Team',
        actionType: 'secondary'
      },
      {
        id: 'alert-3',
        type: 'info',
        title: 'System Maintenance',
        message: 'Scheduled maintenance window starts at 2:00 PM. Expected downtime: 15 minutes.',
        timestamp: new Date(Date.now() - 900000),
        action: 'View Details',
        actionType: 'outline'
      }
    ];

    setAlerts(mockAlerts);
  }, []);

  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  const handleAlertAction = (alert) => {
    console.log(`Executing action for alert: ${alert.id}`);
    // In a real app, this would trigger specific actions
    switch (alert.action) {
      case 'Investigate':
        // Navigate to detailed investigation view
        break;
      case 'Contact Team':
        // Open communication interface
        break;
      case 'View Details':
        // Show detailed information modal
        break;
      default:
        break;
    }
  };

  const getAlertConfig = (type) => {
    switch (type) {
      case 'critical':
        return {
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          iconColor: 'text-error',
          icon: 'AlertTriangle',
          textColor: 'text-error'
        };
      case 'warning':
        return {
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          iconColor: 'text-warning',
          icon: 'AlertCircle',
          textColor: 'text-warning'
        };
      case 'info':
        return {
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20',
          iconColor: 'text-primary',
          icon: 'Info',
          textColor: 'text-primary'
        };
      default:
        return {
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          iconColor: 'text-muted-foreground',
          icon: 'Bell',
          textColor: 'text-muted-foreground'
        };
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.id));

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border-b border-border">
      <div className="px-4 lg:px-6 py-3">
        <div className="space-y-3">
          {visibleAlerts.map((alert) => {
            const config = getAlertConfig(alert.type);
            
            return (
              <div
                key={alert.id}
                className={`flex items-start justify-between p-4 rounded-lg border ${config.bgColor} ${config.borderColor}`}
              >
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-0.5">
                    <Icon 
                      name={config.icon} 
                      size={20} 
                      className={config.iconColor}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`text-sm font-semibold ${config.textColor}`}>
                        {alert.title}
                      </h4>
                      <span className="text-xs text-muted-foreground ml-2">
                        {formatTimeAgo(alert.timestamp)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-foreground mb-3">
                      {alert.message}
                    </p>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant={alert.actionType}
                        size="sm"
                        onClick={() => handleAlertAction(alert)}
                      >
                        {alert.action}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDismissAlert(alert.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Icon name="X" size={14} />
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AlertBanner;